import React, { useEffect, useMemo, useRef, useState } from "react";

import { ConfigureIcon as Configure, MoreIcon as More } from "@packages/ds";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation } from "react-router-dom";

import config from "console_config";
import * as Dropdown from "ds/Dropdown";
import useDecodedParams from "Hooks/useDecodedParams";
import getUrl from "Libs/getUrl";
import { deploymentSizingApiEnabledSelector } from "Reducers/environment/deployment";
import { selectFeatureProfilingEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import { useAutoscalingConfigLink } from "src/organization/pages/project/environment/ConfigureAutoscaling/useAutoscalingConfigLink";
import ShareLink from "src/organization/pages/project/environment/metrics/components/ShareLink";
import { useAppSelector } from "Store/hooks";

import * as S from "./Service.styles";
import ServiceTitle from "./ServiceTitle";
import ServiceUnits from "./ServiceUnits";
import ServiceUnitsMini from "./ServiceUnitsMini";
import { ServiceUnitProps } from "./type";
import { useAutoscallingValues } from "./util";

const hasService = name => window.location.search.includes(`service=${name}`);

const getShareLink = service => {
  const search = new URLSearchParams(location.search);
  search.set("service", service);

  return `${location.origin}${location.pathname}?${search.toString()}`;
};

const Service = ({
  service,
  onBrush,
  hosts,
  isLoading,
  kind,
  actions
}: ServiceUnitProps) => {
  const intl = useIntl();
  const location = useLocation();
  const container = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(true);

  const profilingEnabled = useAppSelector(selectFeatureProfilingEnabled);

  const { organizationId, projectId, environmentId } = useDecodedParams();
  const { hasCapability } = useAutoscallingValues(service.name);

  const isSizingApiEnabled = useAppSelector(state =>
    deploymentSizingApiEnabledSelector(state, {
      organizationId,
      projectId,
      environmentId
    })
  );

  const { autoscalingEnabledFlag, handleConfigureAutoscaling } =
    useAutoscalingConfigLink(service);

  const shouldShowProfiling =
    profilingEnabled &&
    service &&
    ("worker" in service || "web" in service) &&
    config.CUSTOM_BLACKFIRE_CARD_LANGUAGE?.some?.(
      type => service?.type?.toLowerCase().indexOf(type) != -1
    );

  const onToggle = useMemo(
    () => () => {
      setIsOpen(!isOpen);
    },
    [isOpen]
  );

  useEffect(() => {
    if (container.current && hasService(service.name)) {
      container.current.scrollIntoView();
    }
  }, [service.name]);

  const data = service.metrics || {};

  const hasMetrics = Object.entries(data).some(([, value]) => value.length);

  if (!hasMetrics && !isLoading) return null;

  const resourceConfigUrl = getUrl({
    key: "organization.project.environment.configureResources",
    props: {
      organizationId,
      projectId,
      environmentId: environmentId || "",
      location: location.pathname + location.search,
      queryParams: ""
    }
  });

  const profilingPath = `/${organizationId}/${projectId}/${environmentId}/profiling?app=${service.name}`;
  return (
    <>
      <S.Layout ref={container}>
        <S.Header $isOpen={isOpen}>
          <ServiceTitle service={service} />
          <S.Actions>
            {isSizingApiEnabled && (
              <S.configureResources to={resourceConfigUrl}>
                <Configure color="var(--mode-vector-neutral-default)" />
              </S.configureResources>
            )}
            {actions}

            <Dropdown.Root id="services">
              <S.MoreAction aria-label="more-action">
                <More />
              </S.MoreAction>
              <Dropdown.Portal>
                <Dropdown.Content align="end">
                  {service.kind === "webapp" &&
                    hasCapability &&
                    autoscalingEnabledFlag && (
                      <Dropdown.Item onClick={handleConfigureAutoscaling}>
                        {intl.formatMessage({
                          id: "configure.thresholds"
                        })}
                      </Dropdown.Item>
                    )}
                  {shouldShowProfiling && (
                    <Dropdown.LinkItem to={`${profilingPath}`}>
                      <FormattedMessage id="view_profiling" />
                    </Dropdown.LinkItem>
                  )}
                  <Dropdown.Item>
                    <ShareLink link={getShareLink(service.name)} />
                  </Dropdown.Item>
                </Dropdown.Content>
              </Dropdown.Portal>
            </Dropdown.Root>

            <S.ShowMetrics
              onClick={onToggle}
              color="var(--mode-vector-neutral-default)"
              $isOpen={isOpen}
            />
          </S.Actions>
        </S.Header>
        {!isOpen && (
          <S.Grid>
            <ServiceUnitsMini
              service={service}
              isLoading={isLoading}
              kind={kind}
            />
          </S.Grid>
        )}
      </S.Layout>
      {isOpen && (
        <ServiceUnits
          service={service}
          isLoading={isLoading}
          onBrush={onBrush}
          hosts={hosts}
          kind={kind}
        />
      )}
    </>
  );
};

export default React.memo(Service);
